import 'isomorphic-fetch';
import EmailNotificationsApiError from './EmailNotificationsApiError';
import { formatSaveAlertRequest, addVVEmptyString } from '../utils';
import { mapNewsletterActivityStatusToRequest } from './notificationsAdapter';
import { applyPathParamsToUrl, buildQueryParamsString } from '../../common/utils';

const ENDPOINTS = {
  SAVE_NOTIFICATION: '/notifications/email/subscription',
  GET_NOTIFICATIONS: '/notifications/email/all_subscription/{emailAddress}',
  DISABLE_ALL_NOTIFICATIONS: '/notifications/email/disable_all_searches/{emailAddress}',
  UPDATE_SEARCH_NOTIFICATION:
    '/notifications/email/subscription/{emailAddress}/searches/{searchId}/operation/{operation}',
  UPDATE_NEWSLETTER_NOTIFICATION:
    '/notifications/email/subscription/{emailAddress}/newsletterType/{newsletterType}/operation/{operation}',
  DELETE_SEARCH_NOTIFICATION:
    '/notifications/email/subscription/{emailAddress}/searches/{searchId}/operation/delete',
  CREATE_USER: '/notifications/email/create_user',
};

const FETCH_OPTIONS = {
  mode: 'cors',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/x-www-form-urlencoded',
  },
};

export default class ApiNotifications {
  constructor(urlBase) {
    this.urlBase = urlBase;
  }

  static get(url) {
    return this.withErrorHandling(fetch(url, { method: 'GET', ...FETCH_OPTIONS }));
  }

  static patch(url, body) {
    return this.withErrorHandling(fetch(url, { body, method: 'PATCH', ...FETCH_OPTIONS }));
  }

  static post(url, body = null, fetchOptions = FETCH_OPTIONS) {
    return this.withErrorHandling(fetch(url, { body, method: 'POST', ...fetchOptions }));
  }

  static withErrorHandling(promise) {
    return promise
      .then(response =>
        response.json().then(json => {
          if (json.errors) {
            const error = Array.isArray(json.errors) ? json.errors[0] : json.errors;
            const errorMessage = error.details || error.detail || '';
            throw new EmailNotificationsApiError(error.status, errorMessage, response.status);
          }

          // Include attribute is new alert required to send in tracking request
          json.is_new_alert = response.status === 201; // eslint-disable-line no-param-reassign

          return json;
        })
      )
      .catch(err => {
        if (err.name === 'EmailNotificationsApiError') {
          throw err;
        }

        throw new EmailNotificationsApiError('', err.message);
      });
  }

  /**
   * Get user subscription
   *
   * @param {*} data
   */
  getUserSubscription(data) {
    /* eslint-disable camelcase */
    const { country_code, vertical_id, control_value, emailAddress } = data;

    const baseUrl = `${this.urlBase}${ENDPOINTS.GET_NOTIFICATIONS}`;
    const composedPath = applyPathParamsToUrl(baseUrl, { emailAddress });
    const queryParamsString = buildQueryParamsString({
      country_code,
      vertical_id,
      control_value,
    });
    const url = `${composedPath}?${queryParamsString}`;

    return ApiNotifications.get(url);
  }

  updateSearchNotificationActivityStatus(data, notificationId, isEnabled) {
    /* eslint-disable camelcase */
    const { country_code, vertical_id, control_value, emailAddress, ip, vv, browserId } = data;

    const baseUrl = `${this.urlBase}${ENDPOINTS.UPDATE_SEARCH_NOTIFICATION}`;
    const composedPath = applyPathParamsToUrl(baseUrl, {
      emailAddress,
      searchId: notificationId,
      operation: isEnabled ? 'enabled' : 'disabled',
    });
    const body = `${buildQueryParamsString({
      country_code,
      vertical_id,
      control_value,
      browserId,
      vv,
      ip,
    })}${addVVEmptyString(vv)}`;

    return ApiNotifications.patch(composedPath, body);
  }

  updateSearchNotificationFrequency(data, notificationId, frequency) {
    /* eslint-disable camelcase */
    const { country_code, vertical_id, control_value, emailAddress, ip, vv, browserId } = data;

    const baseUrl = `${this.urlBase}${ENDPOINTS.UPDATE_SEARCH_NOTIFICATION}`;
    const composedPath = applyPathParamsToUrl(baseUrl, {
      emailAddress,
      searchId: notificationId,
      operation: frequency,
    });
    const body = `${buildQueryParamsString({
      country_code,
      vertical_id,
      control_value,
      browserId,
      vv,
      ip,
    })}${addVVEmptyString(vv)}`;

    return ApiNotifications.patch(composedPath, body);
  }

  updateNewsletterNotificationActivityStatus(data, update) {
    const { newsletterType, operation } = mapNewsletterActivityStatusToRequest(update);

    /* eslint-disable camelcase */
    const { country_code, vertical_id, control_value, emailAddress } = data;
    const baseUrl = `${this.urlBase}${ENDPOINTS.UPDATE_NEWSLETTER_NOTIFICATION}`;
    const composedPath = applyPathParamsToUrl(baseUrl, {
      emailAddress,
      newsletterType,
      operation,
    });

    const body = buildQueryParamsString({
      country_code,
      vertical_id,
      control_value,
    });

    return ApiNotifications.patch(composedPath, body);
  }

  deleteSearchNotification(data, notification) {
    /* eslint-disable camelcase */
    const { country_code, vertical_id, control_value, emailAddress, ip, vv, browserId } = data;

    const baseUrl = `${this.urlBase}${ENDPOINTS.DELETE_SEARCH_NOTIFICATION}`;
    const composedPath = applyPathParamsToUrl(baseUrl, {
      emailAddress,
      searchId: notification.id,
    });
    const body = `${buildQueryParamsString({
      country_code,
      vertical_id,
      control_value,
      browserId,
      vv,
      ip,
    })}${addVVEmptyString(vv)}`;

    return ApiNotifications.patch(composedPath, body);
  }

  disableAllNotifications(data) {
    /* eslint-disable camelcase */
    const { country_code, vertical_id, control_value, emailAddress, ip, vv, browserId } = data;
    const baseUrl = `${this.urlBase}${ENDPOINTS.DISABLE_ALL_NOTIFICATIONS}`;
    const composedPath = applyPathParamsToUrl(baseUrl, { emailAddress });

    const body = `${buildQueryParamsString({
      country_code,
      vertical_id,
      control_value,
      browserId,
      vv,
      ip,
    })}${addVVEmptyString(vv)}`;

    return ApiNotifications.patch(composedPath, body);
  }

  saveSearchNotification(data) {
    const { searchParameters, trovit, config, userData, totalResults, section } = data;

    const url = `${this.urlBase}${ENDPOINTS.SAVE_NOTIFICATION}`;
    const request = formatSaveAlertRequest(
      searchParameters,
      trovit,
      config,
      userData,
      totalResults,
      section
    );
    const body = JSON.stringify(request);

    return ApiNotifications.post(url, body);
  }

  createUser(data) {
    const url = `${this.urlBase}${ENDPOINTS.CREATE_USER}`;
    const body = buildQueryParamsString(data);

    const fetchOptions = {
      ...FETCH_OPTIONS,
      headers: {
        ...FETCH_OPTIONS.headers,
        Accept: 'application/json',
        'Content-Type': 'application/vnd.api+json',
      },
    };

    return ApiNotifications.post(url, body, fetchOptions);
  }
}



// WEBPACK FOOTER //
// ./app/sdk/notifications/email/ApiNotifications.js